import { P2GBox, P2GLink } from '@parcel2go/core-ui';
import { P2GText } from '@parcel2go/core-ui';
import { useState } from 'react';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { CreateItemReq, CreateItemRes } from '../../../lib/helpers/useItemCreateHelper';
import { uid } from '../../../lib/helpers/uid';
import AlertWithIcon from '../../AlertWithIcon';
import { ExportToCsv } from '../../csv/ExportToCsv';
import { CsvSmartSendMapResult } from '../../csv/MapCsvDataToSmartSendItem';
import SidePanelSection, { SidePanelContent, SidePanelSections } from '../../../../../components/sidepanel-section';
import { CsvFieldInfo } from './CsvFieldInfo';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { CsvCore } from './CsvCore';
import useGoto from '~/hooks/use-goto';
import { RoutingConstants } from '~/apps/smartsend/lib/consts/RoutingConstants';

interface props {
  onSave: (items: CreateItemReq[]) => Promise<CreateItemRes[]>;
  close: () => void;
}

export const CsvPanel: React.FC<props> = (props) => {
  const { goTo } = useGoto();
  const [failed, setFail] = useState<CreateItemRes[]>([]);
  const [originalHeaders, setOriginalHeaders] = useState<string[]>([]);
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  const onSave = async (items: CsvSmartSendMapResult[], originalHeaders: string[]) => {
    const result = await props.onSave(
      items.map((x) => {
        return { id: uid(), item: x.item, raw: x.raw };
      })
    );
    setOriginalHeaders(originalHeaders);
    setFail(result);
  };

  return (
    <SidePanelSections>
      {failed.length > 0 ? (
        <>
          <SidePanelSection>
            <AlertWithIcon color="error">
              <P2GText>
                <Trans
                  t={t}
                  i18nKey="CSV_ITEMS_CREATION_ERROR"
                  values={{ count: failed.length }}
                  defaults={'{{count}} items failed to create'}
                />
              </P2GText>
            </AlertWithIcon>
            <P2GText>
              {t('DOWNLOAD_ITEMS_WITH_ERROR', {
                defaultValue: 'You can download the items which failed to create below.'
              })}
            </P2GText>
            <ExportToCsv
              mode="auto"
              fileName="SmartSend_SelectedItems_Export"
              headers={() => {
                return originalHeaders;
              }}
              data={() => {
                const data = failed.map((x) => {
                  return [...x.raw, x.error];
                });
                return {
                  fields: [...originalHeaders, 'Error'],
                  data: data
                };
              }}
            />
          </SidePanelSection>
        </>
      ) : (
        <SidePanelSection title={t('UPLOAD_CSV_FILE_SUBTITLE', { defaultValue: 'Upload a file' })}>
          <CsvCore onSave={onSave} />
          <P2GBox mt={2}>
            <P2GLink
              onClick={() => {
                goTo(RoutingConstants.CsvMapping);
                props.close();
              }}
            >
              {t('MANAGE_SAVED_MAPPINGS_LINK', { defaultValue: 'Manage Templates' })}
            </P2GLink>
          </P2GBox>
        </SidePanelSection>
      )}

      <SidePanelSection title={t('RECOMMENDED_CSV_FORMAT_SUBTITLE', { defaultValue: 'Recommended Format' })}>
        <SidePanelContent>
          <P2GText component="p">
            {t('CSV_UPLOAD_INTRO_TEXT', {
              defaultValue:
                'Our CSV upload tool is flexible and allows you to upload a CSV file by mapping your columns to the information that we require.'
            })}
          </P2GText>
          <P2GText component="p">
            <Trans
              t={t}
              i18nKey="CSV_UPLOAD_INTRO_TEXT_2"
              components={{ CsvFieldInfo: <CsvFieldInfo /> }}
              defaults={
                'Our default mapped fields can be viewed <CsvFieldInfo>here</CsvFieldInfo>, and you can also define your own when you upload your CSV.'
              }
            />
          </P2GText>
        </SidePanelContent>
      </SidePanelSection>

      {SettingsCache.settings.Features.includes('CsvUploadExamples') && (
        <SidePanelSection title={t('EXAMPLE_FILES_SUBTITLE', { defaultValue: 'Example Files' })}>
          <SidePanelContent>
            {SettingsCache.settings.Downloads.SampleDomesticCsv && (
              <P2GText component="p">
                <Trans
                  t={t}
                  i18nKey="EXAMPLE_DOMESTIC_FILE_DESCRIPTION"
                  components={{
                    a: <P2GLink href={SettingsCache.settings.Downloads.SampleDomesticCsv} target="_blank" />
                  }}
                  defaults={'An example domestic file can be downloaded from <a>here</a>.'}
                />
              </P2GText>
            )}
            {SettingsCache.settings.Downloads.SampleInternationalCsv && (
              <P2GText component="p">
                <Trans
                  t={t}
                  i18nKey="EXAMPLE_INTERNATIONAL_FILE_DESCRIPTION"
                  components={{
                    a: <P2GLink href={SettingsCache.settings.Downloads.SampleInternationalCsv} target="_blank" />
                  }}
                  defaults={'An example international file can be downloaded from <a>here</a>.'}
                />
              </P2GText>
            )}
          </SidePanelContent>
        </SidePanelSection>
      )}
    </SidePanelSections>
  );
};
